/**
 * Firebase AuthContext — replaces custom JWT authentication
 *
 * Features:
 * - Google Sign-In
 * - Email & Password Authentication
 * - Phone Number OTP Authentication
 * - Persistent login sessions (survives browser restarts)
 * - Automatic token refresh
 * - Backend integration with Firebase ID token
 *
 * Backend endpoints:
 *   POST /api/client/auth/firebase-login  → { status, data: { customer, accessToken } }
 *   GET  /api/client/profile              → { status, data: <customer> }
 *   POST /api/client/auth/logout          → { status }
 */

import { createContext, useReducer, useEffect, useContext, useState, useCallback, useRef } from 'react'
import axios from 'axios'
import { setAuthToken } from 'utils'
import { useSnackbar } from 'notistack'
import { API_URL } from 'config'
import { useNavigate } from 'react-router-dom'
import { connectSocket, disconnectSocket } from 'services/socket'
import { collectFingerprint } from 'utils/fingerprint'
import {
  auth,
  googleProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  onAuthStateChanged
} from 'config/firebase'

const authReducer = (state, action) => {
  const { type, payload } = action
  switch (type) {
    case 'USER_LOADED':
      return { ...state, loadingAuth: false, isAuthenticated: true, user: payload.customer, firebaseUser: payload.firebaseUser }
    case 'LOGIN_SUCCESS':
      return { ...state, isAuthenticated: true, loadingAuth: false, user: payload.customer, firebaseUser: payload.firebaseUser }
    case 'EDIT_PROFILE':
      return { ...state, user: { ...state.user, ...payload } }
    case 'LOGOUT':
    case 'AUTH_ERROR':
      return { ...state, isAuthenticated: false, loadingAuth: false, user: null, firebaseUser: null }
    case 'SET_LOADING':
      return { ...state, loadingAuth: payload }
    default:
      return state
  }
}

const initialState = {
  isAuthenticated: null,
  loadingAuth: true,
  user: null,
  firebaseUser: null,
}

const AuthContext = createContext(initialState)

const AuthProvider = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [state, dispatch] = useReducer(authReducer, initialState)
  const navigate = useNavigate()
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null)
  const recaptchaContainerIdRef = useRef('recaptcha-container')
  const confirmationResultRef = useRef(null)

  const ensureRecaptchaContainer = useCallback((containerId = recaptchaContainerIdRef.current) => {
    let container = document.getElementById(containerId)

    if (!container) {
      container = document.createElement('div')
      container.id = containerId
      container.setAttribute('aria-hidden', 'true')
      container.style.position = 'fixed'
      container.style.left = '-9999px'
      container.style.top = '-9999px'
      container.style.width = '1px'
      container.style.height = '1px'
      container.style.overflow = 'hidden'
      document.body.appendChild(container)
    }

    return container
  }, [])

  const clearPhoneAuth = useCallback(() => {
    confirmationResultRef.current = null

    if (recaptchaVerifier) {
      try {
        recaptchaVerifier.clear()
      } catch (error) {
        console.warn('Failed to clear reCAPTCHA verifier:', error)
      }
    }

    setRecaptchaVerifier(null)

    const container = document.getElementById(recaptchaContainerIdRef.current)
    if (container?.parentNode) {
      container.parentNode.removeChild(container)
    }
  }, [recaptchaVerifier])

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in with Firebase - load user WITHOUT logging event (this is a session restore, not a new login)
        await loadUserFromFirebase(firebaseUser, false, false)
      } else {
        // User is signed out
        dispatch({ type: 'AUTH_ERROR' })
        setAuthToken(null)
      }
    })

    return () => unsubscribe()
  }, []) // eslint-disable-line

  // Connect/disconnect socket based on user state
  useEffect(() => {
    if (state.user?.id) {
      connectSocket(state.user.id)
    } else {
      disconnectSocket()
    }
  }, [state.user?.id])

  /**
   * Load user profile from backend using Firebase ID token
   * @param {Object} firebaseUser - Firebase user object
   * @param {boolean} greet - Show welcome message
   * @param {boolean} logEvent - Log this as a new login event (true for actual logins, false for session restores)
   */
  const loadUserFromFirebase = async (firebaseUser, greet = false, logEvent = false) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      
      // Get Firebase ID token
      const idToken = await firebaseUser.getIdToken()
      
      // Only collect fingerprint for actual login events, not session restores
      const fingerprint = logEvent ? await collectFingerprint() : null
      
      // Send token to backend
      const { data } = await axios.post(
        `${API_URL}/client/auth/firebase-login`,
        { idToken, fingerprint, logEvent }, // Send logEvent flag to backend
        { headers: { 'Content-Type': 'application/json' } }
      )
      
      const { customer } = data.data
      
      // Set token for subsequent requests
      setAuthToken(idToken)
      
      dispatch({ 
        type: 'USER_LOADED', 
        payload: { customer, firebaseUser } 
      })

      if (greet && customer?.name) {
        enqueueSnackbar(`Welcome back, ${customer.name}!`, { variant: 'success' })
      }
    } catch (error) {
      console.error('Failed to load user from backend:', error)
      const msg = error.response?.data?.message || 'Authentication failed'
      enqueueSnackbar(msg, { variant: 'error' })
      dispatch({ type: 'AUTH_ERROR' })
      await signOut(auth)
    }
  }

  /**
   * Refresh Firebase ID token and update axios headers
   */
  const refreshToken = async () => {
    try {
      const firebaseUser = auth.currentUser
      if (firebaseUser) {
        const idToken = await firebaseUser.getIdToken(true) // force refresh
        setAuthToken(idToken)
        return idToken
      }
    } catch (error) {
      console.error('Token refresh failed:', error)
      logout({ sessionExpired: true })
    }
    return null
  }

  // Auto-refresh token every 50 minutes (tokens expire after 1 hour)
  useEffect(() => {
    const interval = setInterval(() => {
      if (auth.currentUser) {
        refreshToken()
      }
    }, 50 * 60 * 1000) // 50 minutes

    return () => clearInterval(interval)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const updateUser = (data) => dispatch({ type: 'USER_LOADED', payload: { customer: data, firebaseUser: state.firebaseUser } })

  /**
   * Google Sign-In
   */
  const loginWithGoogle = async ({ onSuccess } = {}) => {
    try {
      const result = await signInWithPopup(auth, googleProvider)
      const firebaseUser = result.user
      
      await loadUserFromFirebase(firebaseUser, true, true) // Log this as a new login event
      onSuccess && onSuccess()
    } catch (error) {
      console.error('Google sign-in error:', error)
      const msg = error.message || 'Google sign-in failed'
      enqueueSnackbar(msg, { variant: 'error' })
      dispatch({ type: 'AUTH_ERROR' })
    }
  }

  /**
   * Email & Password Sign-In
   */
  const loginWithEmail = async ({ email, password, greet = true, onSuccess }) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email.toLowerCase().trim(), password)
      const firebaseUser = result.user
      
      await loadUserFromFirebase(firebaseUser, greet, true) // Log this as a new login event
      onSuccess && onSuccess()
    } catch (error) {
      console.error('Email sign-in error:', error)
      let msg = 'Login failed'
      if (error.code === 'auth/user-not-found') msg = 'No account found with this email'
      if (error.code === 'auth/wrong-password') msg = 'Incorrect password'
      if (error.code === 'auth/invalid-credential') msg = 'Invalid email or password'
      if (error.code === 'auth/too-many-requests') msg = 'Too many failed attempts. Try again later'
      enqueueSnackbar(msg, { variant: 'error' })
      dispatch({ type: 'AUTH_ERROR' })
    }
  }

  /**
   * Legacy login method for backward compatibility
   */
  const login = async ({ email, password, greet = true, onSuccess }) => {
    await loginWithEmail({ email, password, greet, onSuccess })
  }

  /**
   * Phone Number OTP Sign-In
   */
  const initPhoneAuth = useCallback((containerId = recaptchaContainerIdRef.current) => {
    if (recaptchaVerifier) {
      return recaptchaVerifier
    }

    try {
      const container = ensureRecaptchaContainer(containerId)
      const verifier = new RecaptchaVerifier(auth, container, {
        size: 'invisible',
        callback: () => {
          // reCAPTCHA solved
        },
        'expired-callback': () => {
          console.warn('reCAPTCHA expired. Please try again.')
        },
        'error-callback': () => {
          console.error('reCAPTCHA error')
        }
      })

      setRecaptchaVerifier(verifier)
      return verifier
    } catch (error) {
      console.error('Failed to initialize reCAPTCHA:', error)
      if (recaptchaVerifier) {
        return recaptchaVerifier
      }
      throw error
    }
  }, [ensureRecaptchaContainer, recaptchaVerifier])

  const loginWithPhone = async ({ phoneNumber, onCodeSent }) => {
    try {
      const verifier = initPhoneAuth()
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, verifier)
      
      confirmationResultRef.current = confirmationResult
      
      onCodeSent && onCodeSent(confirmationResult)
      enqueueSnackbar('OTP sent to your phone', { variant: 'success' })
      
      return confirmationResult
    } catch (error) {
      console.error('Phone auth error:', error)
      let msg = 'Failed to send OTP'
      if (error.code === 'auth/invalid-phone-number') msg = 'Invalid phone number'
      if (error.code === 'auth/too-many-requests') msg = 'Too many requests. Try again later'
      enqueueSnackbar(msg, { variant: 'error' })
      throw error
    }
  }

  const verifyPhoneOtp = async ({ code, onSuccess }) => {
    try {
      const confirmationResult = confirmationResultRef.current
      if (!confirmationResult) {
        throw new Error('No confirmation result found. Please request OTP again.')
      }
      
      const result = await confirmationResult.confirm(code)
      const firebaseUser = result.user
      confirmationResultRef.current = null
      
      await loadUserFromFirebase(firebaseUser, true, true) // Log this as a new login event
      onSuccess && onSuccess()
    } catch (error) {
      console.error('OTP verification error:', error)
      let msg = 'Invalid OTP'
      if (error.code === 'auth/invalid-verification-code') msg = 'Invalid verification code'
      if (error.code === 'auth/code-expired') msg = 'OTP expired. Request a new one'
      enqueueSnackbar(msg, { variant: 'error' })
      throw error
    }
  }

  /**
   * Logout
   */
  const logout = async ({ sessionExpired = false } = {}) => {
    try {
      clearPhoneAuth()
      await signOut(auth)
      await axios.post(`${API_URL}/client/auth/logout`).catch(() => {})
      dispatch({ type: 'LOGOUT' })
      setAuthToken(null)
      
      sessionExpired
        ? enqueueSnackbar('Session expired, please login again', { variant: 'warning' })
        : enqueueSnackbar('See you soon!', { variant: 'success' })
      
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  /**
   * Register with Email & Password
   */
  const register = async (values) => {
    const { password, name, mobile, phone, email } = values
    try {
      // Create Firebase user
      const result = await createUserWithEmailAndPassword(
        auth, 
        email.toLowerCase().trim(), 
        password
      )
      const firebaseUser = result.user

      // Send additional data to backend
      const idToken = await firebaseUser.getIdToken()
      await axios.post(`${API_URL}/client/auth/firebase-register`, {
        idToken,
        name,
        mobile: mobile || phone || undefined,
      })

      enqueueSnackbar('Registration successful! Logging you in...', { variant: 'success' })
      
      // Auto-login after registration
      await loadUserFromFirebase(firebaseUser, false)
      navigate('/')
    } catch (error) {
      console.error('Registration error:', error)
      let msg = 'Registration failed'
      if (error.code === 'auth/email-already-in-use') msg = 'Email already registered'
      if (error.code === 'auth/weak-password') msg = 'Password is too weak'
      if (error.code === 'auth/invalid-email') msg = 'Invalid email address'
      enqueueSnackbar(msg, { variant: 'error' })
    }
  }

  /**
   * Password Reset
   */
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email.toLowerCase().trim())
      enqueueSnackbar('Password reset email sent. Check your inbox.', { variant: 'success' })
    } catch (error) {
      console.error('Password reset error:', error)
      let msg = 'Failed to send reset email'
      if (error.code === 'auth/user-not-found') msg = 'No account found with this email'
      if (error.code === 'auth/invalid-email') msg = 'Invalid email address'
      enqueueSnackbar(msg, { variant: 'error' })
    }
  }

  /**
   * Edit Profile (backend only, not Firebase profile)
   */
  const editProfile = async (values) => {
    try {
      const { data } = await axios.put(`${API_URL}/client/profile`, values)
      dispatch({ type: 'EDIT_PROFILE', payload: data.data ?? data })
      enqueueSnackbar('Profile updated successfully', { variant: 'success' })
    } catch (error) {
      const msg = error.response?.data?.message || 'Update failed'
      enqueueSnackbar(msg, { variant: 'error' })
      throw error
    }
  }

  /**
   * Load user manually (for refreshing profile data)
   */
  const loadUser = async () => {
    if (auth.currentUser) {
      await loadUserFromFirebase(auth.currentUser, false, false) // Don't log as new login event
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        firebaseUser: state.firebaseUser,
        loadingAuth: state.loadingAuth,
        isAuthenticated: state.isAuthenticated,
        isAdmin: state.user?.role === 'admin',
        
        // Login methods
        login,
        loginWithEmail,
        loginWithGoogle,
        loginWithPhone,
        verifyPhoneOtp,
        initPhoneAuth,
        clearPhoneAuth,
        
        // Other methods
        logout,
        register,
        resetPassword,
        updateUser,
        editProfile,
        loadUser,
        refreshToken,
      }}
      displayName="Authentication"
    >
      {children}
    </AuthContext.Provider>
  )
}

const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) throw new Error('useAuth can only be used inside AuthProvider')
  return context
}

export { AuthProvider, useAuth }
